import DateTimeDisplay from '@/components/DateTime';
import { percentageFormatter } from '@/helpers/percentageFormatter';
import { Badge, Box, Icon, TextLink } from '@palmetto/palmetto-components';
import { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { PriceSheetStatus } from 'types';

import { DiffCell } from './DiffCell';
import { DiffMapListCell } from './DiffMapListCell';

interface ColumnLinkProps {
  onColumnClick: (dataKey: string) => void;
  dataKey: string;
  children: ReactNode;
}

const ColumnLink: FC<ColumnLinkProps> = ({ onColumnClick, dataKey, children }) => (
  <TextLink onClick={() => onColumnClick(dataKey)} style={{ textDecoration: 'underline', color: 'inherit' }}>
    {children}
  </TextLink>
);

const hasDifference = (oldValue: number | undefined, newValue: string) => {
  if (newValue === '' || newValue === undefined) {
    return false;
  }
  const newValueNumber = parseFloat(newValue);
  if ((oldValue || oldValue === 0) && Math.abs(newValueNumber - oldValue) < 0.00001) {
    return false;
  }
  return true;
};

export const getEnergyBackupColumnConfig = (
  values: Record<string, any>,
  onColumnClick: (dataKey: string) => void,
  priceSheetOptions: Record<string, string>,
) => {
  return [
    {
      heading: 'Status',
      dataKey: 'status',
      width: 125,
      render: (_cell: any, row: any) => (
        <Badge
          variant={
            row.status === PriceSheetStatus.active
              ? 'success'
              : row.status === PriceSheetStatus.archived
                ? 'warning'
                : 'danger'
          }
          message={row.status}
        />
      ),
    },
    { heading: 'org', dataKey: 'organizationName' },
    { heading: 'state', dataKey: 'state' },
    {
      heading: 'utility',
      dataKey: 'utility',
      render: (cell: any) => <Box>{cell.name}</Box>,
    },
    {
      heading: (
        <ColumnLink dataKey="priceSheetName" onColumnClick={onColumnClick}>
          Price Sheet
        </ColumnLink>
      ),
      dataKey: 'priceSheetName',
      render: (cell: any, row: any) =>
        values.priceSheet && priceSheetOptions[values.priceSheet] !== cell ? (
          <DiffCell oldValue={cell} newValue={priceSheetOptions[values.priceSheet]} />
        ) : (
          <Link
            to={`/admin/pricing/${row.programType}/price-sheet/${row.priceSheetId}`}
            style={{ textDecoration: 'underline' }}
          >
            {cell}
          </Link>
        ),
    },
    {
      heading: (
        <ColumnLink dataKey="escalationRateMin" onColumnClick={onColumnClick}>
          esc min
        </ColumnLink>
      ),
      dataKey: 'escalationRateMin',
      headerClassName: 'wrap',
      render: (cell: any) =>
        hasDifference(cell, values.escalationRateMin) ? (
          <DiffCell
            oldValue={percentageFormatter.format(cell)}
            newValue={percentageFormatter.format(parseFloat(values.escalationRateMin))}
          />
        ) : (
          <div>{percentageFormatter.format(cell)}</div>
        ),
    },
    {
      heading: (
        <ColumnLink dataKey="escalationRateMax" onColumnClick={onColumnClick}>
          esc max
        </ColumnLink>
      ),
      dataKey: 'escalationRateMax',
      headerClassName: 'wrap',
      render: (cell: any) =>
        hasDifference(cell, values.escalationRateMax) ? (
          <DiffCell
            oldValue={percentageFormatter.format(cell)}
            newValue={percentageFormatter.format(parseFloat(values.escalationRateMax))}
          />
        ) : (
          <div>{cell && percentageFormatter.format(cell)}</div>
        ),
    },
    {
      heading: (
        <ColumnLink dataKey="minTerm" onColumnClick={onColumnClick}>
          min term
        </ColumnLink>
      ),
      dataKey: 'minTerm',
      headerClassName: 'wrap',
      render: (cell: any) =>
        hasDifference(cell, values.minTerm) ? (
          <DiffCell oldValue={`${cell} mo`} newValue={`${values.minTerm} mo`} />
        ) : (
          <div>{cell} mo</div>
        ),
    },
    {
      heading: (
        <ColumnLink dataKey="maxTerm" onColumnClick={onColumnClick}>
          max term
        </ColumnLink>
      ),
      dataKey: 'maxTerm',
      headerClassName: 'wrap',
      render: (cell: any) =>
        hasDifference(cell, values.maxTerm) ? (
          <DiffCell oldValue={`${cell} mo`} newValue={`${values.maxTerm} mo`} />
        ) : (
          <div>{cell} mo</div>
        ),
    },
    {
      heading: (
        <ColumnLink dataKey="minSystemCost" onColumnClick={onColumnClick}>
          min cost
        </ColumnLink>
      ),
      dataKey: 'minSystemCost',
      headerClassName: 'wrap',
      render: (cell: any) =>
        hasDifference(cell, values.minSystemCost) ? (
          <DiffCell
            oldValue={`$${cell?.toLocaleString()}`}
            newValue={`$${parseFloat(values.minSystemCost).toLocaleString()}`}
          />
        ) : (
          <div>${cell?.toLocaleString()}</div>
        ),
    },
    {
      heading: (
        <ColumnLink dataKey="maxSystemCost" onColumnClick={onColumnClick}>
          max cost
        </ColumnLink>
      ),
      dataKey: 'maxSystemCost',
      headerClassName: 'wrap',
      render: (cell: any) =>
        hasDifference(cell, values.maxSystemCost) ? (
          <DiffCell
            oldValue={`$${cell?.toLocaleString()}`}
            newValue={`$${parseFloat(values.maxSystemCost).toLocaleString()}`}
          />
        ) : (
          <div>${cell?.toLocaleString()}</div>
        ),
    },
    {
      heading: (
        <ColumnLink dataKey="constructionAdders" onColumnClick={onColumnClick}>
          construction adders
        </ColumnLink>
      ),
      dataKey: 'constructionAdders',
      headerClassName: 'wrap',
      render: (cell: any) => (
        <DiffMapListCell
          oldValue={{
            backupBatteryPriceCap: cell?.find((adder: any) => adder.type === 'backupBatteryPriceCap'),
            arbitrageBatteryPriceCap: cell?.find((adder: any) => adder.type === 'arbitrageBatteryPriceCap'),
          }}
          newValue={{
            backupBatteryPriceCap: values.backupBatteryPriceCap,
            arbitrageBatteryPriceCap: values.arbitrageBatteryPriceCap,
          }}
        />
      ),
    },
    {
      heading: 'Created',
      dataKey: 'createdDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.createdDate} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: 'Archived',
      dataKey: 'archivedDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.archivedDate} dateFormat={'DD'} excludeTime />,
    },
    {
      render: (_cell: any, row: any) =>
        row.status !== 'archived' && (
          <Link
            to={`/admin/pricing/${row.programType}/mappings/edit/${row.id}`}
            style={{ textDecoration: 'underline' }}
          >
            <Icon color="grey-600" name="settings" size="sm" />
          </Link>
        ),
      dataKey: 'edit',
    },
  ];
};
